import { Component, OnInit, OnDestroy, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";
import { SpinnerDialogComponent } from "src/app/shared/components/spinner-dialog/spinner-dialog.component";
import { DocumentWorkflowTask } from "src/app/shared/models/document-workflow-task.model";
import { DocumentWorkflowService } from "src/app/shared/services/document-workflow.service";
import { MessageService } from "src/app/shared/services/message.service";
import { UIState } from "src/app/shared/state/ui.state";

class LetterScheduleListEntry extends DocumentWorkflowTask {
  TypeDescription: string;

  ContextUrl: string;  
  ContextDescription: string;
  DossierUrl: string;
}

@Component({
  selector: "app-letter-schedule-list",
  templateUrl: "./letter-schedule-list.component.html",
})
export class LetterScheduleListComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];

  filterText: string = "";
  loading = true;
  spinnerDialogRef: MatDialogRef<SpinnerDialogComponent>;

  displayedColumns: string[] = ["SendDate", "Type", "ContextBuilding", "ContextInfo", "LastAttemptDate", "LastAttemptError", "Actions"];
  data: LetterScheduleListEntry[] = [];
  dataSource: MatTableDataSource<LetterScheduleListEntry> = new MatTableDataSource<LetterScheduleListEntry>([]);
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private documentWorkflowService: DocumentWorkflowService,
    private uiState: UIState,
    private messageService: MessageService,
    private matDialog: MatDialog
  ) {
  }

  ngOnInit(): void {
    this.uiState.breadcrumbs$.next([
      {
        name: "Brievenplanning",
        routerLink: "/brievenplanning",
      }
    ]);

    this.reload();
  }

  reload(): void {
    this.subscriptions.push(this.documentWorkflowService.getScheduleList()
      .pipe(
        take(1),
        tap((definitions: DocumentWorkflowTask[]) => {
          this.data = definitions.map(x => {
            const entry = new LetterScheduleListEntry();
            Object.assign(entry, x);

            entry.TypeDescription = this.getTypeName(x.Type);
            entry.ContextDescription = this.getContextDescription(x);
            entry.ContextUrl = this.getContextUrl(x);
            entry.DossierUrl = this.getDossierUrl(x);

            return entry;
          });

          this.closeSpinner();
          this.removeFilter();
          this.loading = false;
        })
      )
      .subscribe()
    );
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  getTypeName(type: number): string {
    switch (type) {
      case 1:
        return "eSignFlow";
    
      case 2:
        return "Digitaal Ondertekenen";
    
      case 3:
        return "Documentendienst";
  
      default:
        return "Onbekend";
    }
  }

  applyFilter(): void {
    const filteredData = this.data.filter(x => x.ContextDescription.toLowerCase().includes(this.filterText.toLowerCase()) || x.ContextBuildingCode.toLowerCase().includes(this.filterText.toLowerCase()));

    this.dataSource = new MatTableDataSource(filteredData);
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  removeFilter(): void {
    this.filterText = "";
    this.applyFilter();
  }

  sendItem(item: DocumentWorkflowTask): void {
    if (!item) { 
      return;
    }

    this.messageService.openConfirmationDialog(
      "Geplande verzending versturen",
      "Weet je zeker dat je deze geplande verzending onmiddelijk wilt versturen?"
    ).pipe(
      take(1),
      filter(r => r),
      tap(() => this.openSpinner("Bezig met versturen van de brief...")),
      switchMap(() => this.documentWorkflowService.sendScheduled(item.Id)),
      tap(() => this.messageService.showSnackBarInfo("Brief is verzonden")),
      tap(() => this.reload())
    ).subscribe();
  }

  cancelItem(item: DocumentWorkflowTask): void {
    if (!item) { 
      return;
    }

    this.messageService.openConfirmationDialog(
      "Geplande verzending annuleren",
      "Weet je zeker dat je deze geplande verzending wilt annuleren? Deze actie kan niet ongedaan worden gemaakt."
    ).pipe(
      take(1),
      filter(r => r),
      tap(() => this.openSpinner("Bezig met annuleren van de verzending...")),
      switchMap(() => this.documentWorkflowService.cancelScheduled(item.Id)),
      tap(() => this.messageService.showSnackBarWarning("Verzending is geannuleerd")),
      tap(() => this.reload())
    ).subscribe();
  }

  openSpinner(message: string): void {
    this.spinnerDialogRef = this.matDialog.open(SpinnerDialogComponent, {
      width: "500px",
      disableClose: true,
      data: {
        title: message,
      },
    });
  }

  closeSpinner(): void {
    if (!this.spinnerDialogRef) {
      return;
    }

    this.spinnerDialogRef.close();
    this.spinnerDialogRef = null;
  }

  openUrl(url: string): void {
    window.open(url, "_blank");
  }

  getContextDescription(entry: DocumentWorkflowTask): string {
    if (!entry || !entry.ContextType || !entry.ContextCode) {
      return "";
    }

    switch (entry.ContextType) {
      case "CTN":
        return `Contact ${entry.ContextCode}`;

      case "TVLS":
        return `Technisch verslag leegstand ${entry.ContextCode}`;

      case "TVVW":
        return `Technisch verslag verwaarlozing ${entry.ContextCode}`;

      case "AALS":
        return `Administratieve akte leegstand ${entry.ContextCode}`;

      case "AAVW":
        return `Administratieve akte verwaarlozing ${entry.ContextCode}`;

      case "EIG":
        return `Eigenaar ${entry.ContextCode}`;

      case "INV\\SCHR":
        return `Schrapping op inventaris ${entry.ContextCode}`;

      case "INV\\HER":
        return `Herinnering op inventaris ${entry.ContextCode}`;

      case "INV\\BEZW":
        return `Bezwaar op inventaris ${entry.ContextCode}`;

      case "INV\\ADV":
        return `Advies bezwaren FD op inventaris ${entry.ContextCode}`;

      case "VRIJST\\LS":
      case "VRIJST\\VW":
      case "VRIJST\\OO":
      case "VRIJST\\SR":
        return `Vrijstelling ${entry.ContextCode}`;
    }
  }

  getDossierUrl(entry: DocumentWorkflowTask): string {
    if (!entry || !entry.ContextType || !entry.ContextCode) {
      return;
    }

    return `/dossiers/${entry.ContextBuildingCode}`;
  }

  getContextUrl(entry: DocumentWorkflowTask): string {
    if (!entry || !entry.ContextType || !entry.ContextCode) {
      return;
    }

    switch (entry.ContextType) {
      case "CTN":
        return `/dossiers/${entry.ContextBuildingCode}/contacten`;

      case "TVLS":
        return `/dossiers/${entry.ContextBuildingCode}/technische-verslagen/${entry.ContextCode}?module=leegstand`;

      case "TVVW":
        return `/dossiers/${entry.ContextBuildingCode}/technische-verslagen/${entry.ContextCode}?module=verwaarlozing`;

      case "AALS":
      case "AAVW":
        return `/dossiers/${entry.ContextBuildingCode}/administratieve-aktes/${entry.ContextCode}`;

      case "EIG":
        return `/eigenaars/${entry.ContextCode}`;

      case "INV\\SCHR":
      case "INV\\HER":
      case "INV\\BEZW":
      case "INV\\ADV":
        return `/dossiers/${entry.ContextBuildingCode}`;

      case "VRIJST\\LS":
      case "VRIJST\\VW":
      case "VRIJST\\OO":
      case "VRIJST\\SR":
        return `/dossiers/${entry.ContextBuildingCode}/vrijstellingen/${entry.ContextCode}`;
    }

    return `/dossiers/${entry.ContextBuildingCode}`;
  }
}